import { useCallback, useEffect, useRef } from 'react';

import { CORRUPTION_PHASES, purgeCorruptionState } from '../map/corruptionSystem';
import { SPIDER_BOSS_ENEMY_ID } from '../boss/spiderBossConfig';
import {
  MONSIGNOR_BOSS_ENEMY_ID,
  PRIME_VESSEL_BOSS_ENEMY_ID,
  BLACKHAND_BOSS_ENEMY_ID,
  MONSIGNOR_GUARD_COORDS,
  countMonsignorAliveGuards,
  liberateMonsignorGuards,
} from '../boss/specialBossFloorConfig';
import { relicsPool } from '../data/relics';
import { pickRelicsByRarity } from '../relics/relicRarity';
import { applyRestHooks } from '../../relicEffects';
import { makeRng } from '../../utils/makeRng';

const PULSE_MS = 800;
const POTION_HEAL_AMOUNT = 60;
const REST_SANITY_PCT = 2;

function getPercentRestHeal(maxValue, pct) {
  const max = Number(maxValue) || 0;
  const percent = Number(pct) || 0;
  if (max <= 0 || percent === 0) return 0;
  return Math.max(1, Math.round((max * percent) / 100));
}
const MID_RUN_BOSS_REWARD_ENEMY_IDS = new Set(['nested_horror', MONSIGNOR_BOSS_ENEMY_ID, PRIME_VESSEL_BOSS_ENEMY_ID, BLACKHAND_BOSS_ENEMY_ID]);
const PRIME_VESSEL_DEVICE_ENEMY_IDS = new Set([
  'prime_crucible_device',
  'pulse_engine_device',
  'synaptic_retort_device',
  'astral_reliquary_device',
]);

function isYahakaEnemyId(enemyId) {
  return typeof enemyId === 'string' && enemyId.startsWith('yahaka_voidfeeder');
}

function getYahakaVariantNumber(enemyId) {
  const match = String(enemyId || '').match(/_(\d+)$/);
  const value = Math.floor(Number(match?.[1] || 0));
  return Number.isFinite(value) && value > 0 ? value : 0;
}


function applyLampKeeperRoomLightResultToTorch(roomLightTier, currentFloorTorchDuration, setTurns) {
  if (typeof setTurns !== 'function') return;
  const normalized = String(roomLightTier || '').trim().toLowerCase();
  const duration = Math.max(1, Math.floor(Number(currentFloorTorchDuration) || 0));
  if (!duration) return;

  const targetProgress =
    normalized === 'dark'
      ? 0.2
      : normalized === 'bright'
        ? 0.8
        : normalized === 'steady'
          ? 0.5
          : null;

  if (targetProgress == null) return;

  const nextTurns = Math.round(duration * (1 - targetProgress));
  setTurns(Math.max(0, Math.min(duration, nextTurns)));
}

export function useDungeonCombatFlow({
  combatEnemyTile,
  setCombatEnemyTile,
  map,
  setMap,
  floorCorruption,
  setFloorCorruption,
  setYahakaState,
  setYahakaDefeatCount,
  setYahakaBannerText,
  spiderBossState,
  setSpiderBossState,
  setCorruptionBannerText,
  setPendingCorruptionBonusReward,
  slainEnemiesThisFloor,
  setSlainEnemiesThisFloor,
  floorCleansingTotal,
  setPlayerDeckIds,
  setHp,
  setSanity,
  setTeeth,
  maxTeeth,
  setKeys,
  maxKeys,
  potions,
  setPotions,
  maxPotions,
  setTorches,
  maxTorches,
  hp,
  maxHp,
  maxSp,
  setIsCombatActive,
  setActiveCombatMods,
  preCombatPosRef,
  setPlayerPos,
  awardCombatVictoryXp,
  tarotNoExp,
  tarotChainExpBonus,
  floorLevel,
  runSeedRef,
  restingOnTile,
  ownedRelics,
  setRelics,
  yahakaDefeatCount,
  pickYahakaVictoryBannerLine,
  setNextCombatCost0,
  setTurns,
  currentFloorTorchDuration,
  setFloorActionCount,
  setPulse,
}) {
  const pulseTimeoutRef = useRef(null);

  const clearPulseTimeout = useCallback(() => {
    if (pulseTimeoutRef.current) {
      window.clearTimeout(pulseTimeoutRef.current);
      pulseTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      clearPulseTimeout();
    };
  }, [clearPulseTimeout]);

  const flashPulse = useCallback(() => {
    setPulse(true);
    clearPulseTimeout();
    pulseTimeoutRef.current = window.setTimeout(() => {
      pulseTimeoutRef.current = null;
      setPulse(false);
    }, PULSE_MS);
  }, [clearPulseTimeout, setPulse]);

  const handleCombatVictory = useCallback(
    (_rewardCard, nextDeckIds, finalStats, loot) => {
      let didSlayEnemy = false;
      let shouldRestoreLiberatedMonsignorSp = false;
      const yahakaVariantNumber = getYahakaVariantNumber(combatEnemyTile?.enemyId);
      const wasYahaka = isYahakaEnemyId(combatEnemyTile?.enemyId);
      const isPrimeVesselDevice = PRIME_VESSEL_DEVICE_ENEMY_IDS.has(String(combatEnemyTile?.enemyId || ''));
      const isFinalYahakaForm = yahakaVariantNumber >= 7;

      if (combatEnemyTile) {
        const newMap = map.map((row) => row.slice());
        const { x, y } = combatEnemyTile;

        const wasCorruptionCollapseBoss =
          combatEnemyTile.enemyId === 'nested_horror' &&
          floorCorruption?.bossSpawned &&
          floorCorruption?.phase === CORRUPTION_PHASES.COLLAPSE;

        const wasSpiderBoss =
          combatEnemyTile.enemyId === SPIDER_BOSS_ENEMY_ID &&
          spiderBossState?.enabled &&
          spiderBossState?.bossSpawned &&
          !spiderBossState?.bossDefeated;

        const defeatedEnemyTile = newMap[y]?.[x];
        const defeatedBossTile = defeatedEnemyTile === 'enemy-boss';
        const shouldLiberateMonsignorGuards =
          combatEnemyTile.enemyId === MONSIGNOR_BOSS_ENEMY_ID &&
          defeatedBossTile &&
          countMonsignorAliveGuards(map) === MONSIGNOR_GUARD_COORDS.length;

        shouldRestoreLiberatedMonsignorSp = shouldLiberateMonsignorGuards;

        if (newMap[y] && (defeatedEnemyTile === 'enemy' || defeatedBossTile)) {
          newMap[y][x] =
            wasCorruptionCollapseBoss || wasSpiderBoss || defeatedBossTile
              ? 'steps-down'
              : 'floor';
          if (shouldLiberateMonsignorGuards) {
            const liberatedMap = liberateMonsignorGuards(newMap);
            if (Array.isArray(liberatedMap)) {
              for (let rowIndex = 0; rowIndex < liberatedMap.length; rowIndex += 1) {
                newMap[rowIndex] = liberatedMap[rowIndex];
              }
            }
          }
          didSlayEnemy = true;
          if (!wasYahaka && !isPrimeVesselDevice) {
            setSlainEnemiesThisFloor((count) => count + 1);
          }
        }

        setMap(newMap);
        setCombatEnemyTile(null);

        if (wasYahaka) {
          setYahakaState({
            active: false,
            x: null,
            y: null,
          });

          setYahakaDefeatCount((prev) => {
            const nextValue = Math.max(prev, yahakaVariantNumber || Math.max(0, Math.floor(Number(yahakaDefeatCount) || 0) + 1));
            return Math.min(7, nextValue);
          });

          if (isFinalYahakaForm) {
            setYahakaBannerText(
              typeof pickYahakaVictoryBannerLine === 'function'
                ? pickYahakaVictoryBannerLine()
                : 'The Shadow Beast has been slain'
            );
          }
        }

        if (wasCorruptionCollapseBoss) {
          setFloorCorruption((prev) => purgeCorruptionState(prev));
          setCorruptionBannerText('The corruption purges.');
          setPendingCorruptionBonusReward(true);
        }

        if (wasSpiderBoss) {
          setSpiderBossState((prev) =>
            prev
              ? {
                  ...prev,
                  bossDefeated: true,
                  bossSpawned: false,
                  bossPos: null,
                  bossUnderTile: 'floor',
                  bossLockedOnPlayer: false,
                }
              : prev
          );
        }
      }

      if (Array.isArray(nextDeckIds)) {
        setPlayerDeckIds(nextDeckIds);
      }

      if (finalStats && typeof finalStats.hp === 'number') {
        setHp(Math.floor(finalStats.hp));
      }

      if (shouldRestoreLiberatedMonsignorSp) {
        setSanity(Math.floor(Math.max(0, Number(maxSp) || 0)));
      } else if (finalStats && typeof finalStats.sp === 'number') {
        setSanity(Math.floor(finalStats.sp));
      }

      applyLampKeeperRoomLightResultToTorch(finalStats?.roomLightTier, currentFloorTorchDuration, setTurns);

      if (loot && !wasYahaka) {
        const teethGain = Number(loot?.teeth || 0);
        const keysGain = Number(loot?.keys || 0);
        const potionsGain = Number(loot?.potions || 0);
        const torchesGain = Number(loot?.torches || 0);

        if (teethGain) {
          setTeeth((teeth) => Math.min(maxTeeth, teeth + teethGain));
        }
        if (keysGain) {
          setKeys((keys) => Math.min(maxKeys, keys + keysGain));
        }
        if (potionsGain) {
          setPotions((value) => Math.min(maxPotions, value + potionsGain));
        }
        if (torchesGain) {
          setTorches((value) => Math.min(maxTorches, value + torchesGain));
        }
      }

      setIsCombatActive(false);
      setActiveCombatMods(null);

      const defeatedEnemyId = String(combatEnemyTile?.enemyId || '');
      const suppressAllCombatRewards = PRIME_VESSEL_DEVICE_ENEMY_IDS.has(defeatedEnemyId);
      const shouldOfferMidRunBossRelics = MID_RUN_BOSS_REWARD_ENEMY_IDS.has(defeatedEnemyId);

      if (shouldOfferMidRunBossRelics) {
        const eligibleRareOrMythicRelics = relicsPool.filter(
          (relic) =>
            relic &&
            relic.id &&
            (relic.rarity === 'rare' || relic.rarity === 'mythic') &&
            !ownedRelics.some((owned) => owned.id === relic.id)
        );

        if (eligibleRareOrMythicRelics.length > 0) {
          const rng = makeRng(
            ((runSeedRef.current ?? 0) ^
              (floorLevel * 0x45d9f3b) ^
              ((combatEnemyTile?.x ?? 0) * 0x9e37) ^
              ((combatEnemyTile?.y ?? 0) * 0x85eb) ^
              0x27d4eb2f) >>> 0
          );

          const rareCount = eligibleRareOrMythicRelics.filter(
            (relic) => relic?.rarity === 'rare'
          ).length;
          const mythicCount = eligibleRareOrMythicRelics.filter(
            (relic) => relic?.rarity === 'mythic'
          ).length;

          const picks = pickRelicsByRarity(
            eligibleRareOrMythicRelics,
            Math.min(3, eligibleRareOrMythicRelics.length),
            rng,
            {
              common: 0,
              uncommon: 0,
              rare: rareCount,
              mythic: mythicCount,
            }
          );

          if (picks.length > 0) {
            setRelics(picks);
          }
        }
      }

      const nextSlainCount =
        slainEnemiesThisFloor + (didSlayEnemy && !wasYahaka && !suppressAllCombatRewards ? 1 : 0);

      if (wasYahaka && isFinalYahakaForm) {
        const eligibleMythics = relicsPool.filter(
          (relic) =>
            relic &&
            relic.id &&
            relic.rarity === 'mythic' &&
            !ownedRelics.some((owned) => owned.id === relic.id)
        );

        if (eligibleMythics.length > 0) {
          const rng = makeRng(
            ((runSeedRef.current ?? 0) ^
              (floorLevel * 0x7f4a7c15) ^
              ((combatEnemyTile?.x ?? 0) * 0x9e37) ^
              ((combatEnemyTile?.y ?? 0) * 0x85eb) ^
              0x5f3759df) >>> 0
          );

          const picks = pickRelicsByRarity(
            eligibleMythics,
            Math.min(3, eligibleMythics.length),
            rng,
            { common: 0, uncommon: 0, rare: 0, mythic: 1 }
          );

          setRelics(picks);
        }
      }

      if (!wasYahaka && !suppressAllCombatRewards) {
        awardCombatVictoryXp?.({
          didSlayEnemy,
          nextSlainCount,
          floorCleansingTotal,
          tarotNoExp,
          tarotChainExpBonus,
        });
      }

      preCombatPosRef.current = null;
    },
    [
      awardCombatVictoryXp,
      combatEnemyTile,
      floorCorruption,
      floorCleansingTotal,
      floorLevel,
      map,
      ownedRelics,
      pickYahakaVictoryBannerLine,
      runSeedRef,
      spiderBossState,
      maxKeys,
      maxPotions,
      maxSp,
      maxTeeth,
      maxTorches,
      preCombatPosRef,
      currentFloorTorchDuration,
      setActiveCombatMods,
      setCombatEnemyTile,
      setCorruptionBannerText,
      setFloorCorruption,
      setRelics,
      setYahakaBannerText,
      setYahakaDefeatCount,
      setYahakaState,
      setHp,
      setSpiderBossState,
      setIsCombatActive,
      setKeys,
      setMap,
      setPendingCorruptionBonusReward,
      setPlayerDeckIds,
      setPotions,
      setSanity,
      setSlainEnemiesThisFloor,
      setTeeth,
      setTorches,
      setTurns,
      slainEnemiesThisFloor,
      tarotChainExpBonus,
      tarotNoExp,
      yahakaDefeatCount,
    ]
  );

  const handleCombatDefeat = useCallback(
    (finalStats) => {
      if (finalStats && typeof finalStats.hp === 'number') {
        setHp(Math.floor(finalStats.hp));
      }

      if (finalStats && typeof finalStats.sp === 'number') {
        setSanity(Math.floor(finalStats.sp));
      }

      applyLampKeeperRoomLightResultToTorch(finalStats?.roomLightTier, currentFloorTorchDuration, setTurns);

      setIsCombatActive(false);

      if (preCombatPosRef.current) {
        setPlayerPos(preCombatPosRef.current);
      }

      setCombatEnemyTile(null);
      preCombatPosRef.current = null;
      setActiveCombatMods(null);
    },
    [
      preCombatPosRef,
      currentFloorTorchDuration,
      setActiveCombatMods,
      setCombatEnemyTile,
      setHp,
      setIsCombatActive,
      setPlayerPos,
      setSanity,
      setTurns,
    ]
  );

  const triggerRest = useCallback(() => {
    if (!restingOnTile) return;

    setSanity((sanity) => sanity + getPercentRestHeal(maxSp, REST_SANITY_PCT));

    applyRestHooks(ownedRelics, {
      maxHp,
      maxSp,
      addSanity: (amount) => setSanity((sanity) => sanity + amount),
      addHp: (amount) => setHp((health) => health + amount),
      addKeys: (amount) => setKeys((keys) => Math.min(maxKeys, keys + amount)),
      addTeeth: (amount) => setTeeth((teeth) => Math.min(maxTeeth, teeth + amount)),
      addPotions: (amount) =>
        setPotions((value) => Math.min(maxPotions, value + amount)),
      addNextCombatCost0: (amount) =>
        setNextCombatCost0((value) => Math.max(value, amount)),
    });

    setTurns((turns) => turns + 1);
    setFloorActionCount((count) => count + 1);
    flashPulse();
  }, [
    flashPulse,
    maxHp,
    maxKeys,
    maxPotions,
    maxSp,
    maxTeeth,
    ownedRelics,
    restingOnTile,
    setFloorActionCount,
    setHp,
    setKeys,
    setNextCombatCost0,
    setPotions,
    setSanity,
    setTeeth,
    setTurns,
  ]);

  const usePotionFromBackpack = useCallback(
    (event) => {
      event?.preventDefault?.();
      event?.stopPropagation?.();

      if (potions <= 0) return;
      if (hp >= maxHp) return;

      setPotions((value) => Math.max(0, value - 1));
      setHp((health) => health + POTION_HEAL_AMOUNT);
      setTurns((turns) => turns + 1);
      setFloorActionCount((count) => count + 1);
      flashPulse();
    },
    [flashPulse, hp, maxHp, potions, setFloorActionCount, setHp, setPotions, setTurns]
  );

  return {
    handleCombatVictory,
    handleCombatDefeat,
    triggerRest,
    usePotionFromBackpack,
  };
}
